// Gamepad polling (§8.2, §26): Standard mapping, polled once per frame. Edges are detected by
// comparing with the previous poll and accumulate until the next sampled tick takes them, so a
// press is never lost between ticks. The most recently used pad is the active one; its id string
// picks the label family. Rumble goes through `vibrationActuator.playEffect('dual-rumble')`.
import { TUNE } from '@shared/constants';
import { setV } from '@shared/math';
import type { Vec2 } from '@shared/types';
import { padFamilyFromId, type PadFamily } from './glyphs';

export const PAD_BUTTONS = 17;
/** A button counts as down above this value (analog triggers). */
const PRESS_THRESHOLD = 0.5;
const RELEASE_THRESHOLD = 0.35;
/** Any stick beyond this counts as pad activity (device switching). */
const ACTIVITY_STICK = 0.35;

/** Standard mapping button indices (§8.2). */
export const PadButton = {
  A: 0, B: 1, X: 2, Y: 3, LB: 4, RB: 5, LT: 6, RT: 7, VIEW: 8, START: 9, LS: 10, RS: 11,
  UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15, HOME: 16,
} as const;

/** Minimal Gamepad shape we read (lets tests pass plain objects through a `navigator.getGamepads` stub). */
export interface PadLike {
  id: string;
  index: number;
  connected: boolean;
  mapping?: string;
  timestamp?: number;
  buttons: readonly { pressed: boolean; value: number }[];
  axes: readonly number[];
  vibrationActuator?: { playEffect?(type: string, params: Record<string, number>): Promise<unknown> } | null;
}

export type PadSource = () => readonly (PadLike | null)[];

function defaultSource(): readonly (PadLike | null)[] {
  try {
    return typeof navigator !== 'undefined' && typeof navigator.getGamepads === 'function' ? (navigator.getGamepads() as readonly (PadLike | null)[]) : [];
  } catch {
    return [];
  }
}

/**
 * Radial deadzone with rescale (§8.2): magnitudes below `dz` read 0, the rest maps linearly onto
 * 0..1 so the stick reaches full speed at the rim and moves smoothly just past the deadzone.
 */
export function radialDeadzone(x: number, y: number, dz: number, out: Vec2): Vec2 {
  const m = Math.hypot(x, y);
  if (m <= dz || m === 0) return setV(out, 0, 0);
  const scaled = Math.min(1, (m - dz) / (1 - dz));
  return setV(out, (x / m) * scaled, (y / m) * scaled);
}

export class GamepadInput {
  /** Index into getGamepads() of the active pad, or -1. */
  private active = -1;
  private readonly down = new Uint8Array(PAD_BUTTONS);
  private readonly presses = new Uint16Array(PAD_BUTTONS);
  private readonly releases = new Uint16Array(PAD_BUTTONS);
  private readonly downSince = new Float64Array(PAD_BUTTONS);
  private readonly axes = new Float64Array(4);
  private readonly aimHold = { x: 0, y: 0, at: -Infinity };
  private pad: PadLike | null = null;
  private familyValue: PadFamily = 'xbox';
  private lastIds = '';
  /** True when the last poll saw any input from the active pad (device switching). */
  activity = false;
  /** Changes in the connected set since the last poll (for toasts / glyph refresh). */
  connectionChanged = false;

  constructor(private readonly source: PadSource = defaultSource) {}

  get connected(): boolean {
    return this.pad !== null;
  }

  get family(): PadFamily {
    return this.familyValue;
  }

  get id(): string {
    return this.pad?.id ?? '';
  }

  /** Reads every pad; the one with fresh input becomes active. Returns true when any pad had input. */
  poll(nowMs: number): boolean {
    const pads = this.source();
    let ids = '';
    let chosen: PadLike | null = null;
    // Prefer a pad with input this frame, else keep the current one, else the first connected.
    for (const p of pads) {
      if (!p || !p.connected) continue;
      ids += `${p.index}:${p.id};`;
      if (p.index !== this.active && hasInput(p)) chosen = p;
    }
    this.connectionChanged = ids !== this.lastIds;
    this.lastIds = ids;
    if (!chosen) chosen = pads.find((p) => p && p.connected && p.index === this.active) ?? pads.find((p) => p && p.connected) ?? null;
    if (!chosen) {
      if (this.pad) this.releaseAll();
      this.pad = null;
      this.active = -1;
      this.activity = false;
      return false;
    }
    if (chosen.index !== this.active) {
      this.releaseAll();
      this.active = chosen.index;
      this.familyValue = padFamilyFromId(chosen.id);
      if (this.familyValue === 'generic') this.familyValue = 'xbox';
    }
    this.pad = chosen;
    let any = false;
    const n = Math.min(PAD_BUTTONS, chosen.buttons.length);
    for (let i = 0; i < n; i++) {
      const b = chosen.buttons[i];
      const v = b.pressed ? Math.max(b.value, 1) : b.value;
      const wasDown = this.down[i] === 1;
      const isDown = wasDown ? v > RELEASE_THRESHOLD || b.pressed : v > PRESS_THRESHOLD || (b.pressed && b.value === 0);
      if (isDown && !wasDown) {
        this.down[i] = 1;
        this.presses[i]++;
        this.downSince[i] = nowMs;
        any = true;
      } else if (!isDown && wasDown) {
        this.down[i] = 0;
        this.releases[i]++;
      }
      if (isDown) any = true;
    }
    for (let i = 0; i < 4; i++) this.axes[i] = finite(chosen.axes[i]);
    if (Math.hypot(this.axes[0], this.axes[1]) > ACTIVITY_STICK || Math.hypot(this.axes[2], this.axes[3]) > ACTIVITY_STICK) any = true;
    this.activity = any;
    return any;
  }

  isDown(btn: number): boolean {
    return this.down[btn] === 1;
  }

  /** How long `btn` has been held (ms), or 0. */
  heldMs(btn: number, nowMs: number): number {
    return this.down[btn] === 1 ? nowMs - this.downSince[btn] : 0;
  }

  /** Presses of `btn` since the last take. */
  takePresses(btn: number): number {
    const n = this.presses[btn];
    this.presses[btn] = 0;
    return n;
  }

  peekPresses(btn: number): number {
    return this.presses[btn];
  }

  takeReleases(btn: number): number {
    const n = this.releases[btn];
    this.releases[btn] = 0;
    return n;
  }

  clearEdges(): void {
    this.presses.fill(0);
    this.releases.fill(0);
  }

  /** Left stick with the radial deadzone (§8.2). Screen-down is +y, which is sim +y (§2.1). */
  move(deadzone: number, out: Vec2): Vec2 {
    return radialDeadzone(this.axes[0], this.axes[1], deadzone, out);
  }

  /** Raw left stick (menu navigation). */
  rawLeft(out: Vec2): Vec2 {
    return setV(out, this.axes[0], this.axes[1]);
  }

  /**
   * Right-stick aim (§8.2): deadzone 0.25; after release the last direction is held for 150 ms so
   * a flick-and-press still aims. (0, 0) = no aim (use facing).
   */
  aim(nowMs: number, out: Vec2): Vec2 {
    const a = TUNE.input;
    const x = this.axes[2];
    const y = this.axes[3];
    const m = Math.hypot(x, y);
    if (m > a.padAimDeadzone) {
      this.aimHold.x = x / m;
      this.aimHold.y = y / m;
      this.aimHold.at = nowMs;
      return setV(out, this.aimHold.x, this.aimHold.y);
    }
    if (nowMs - this.aimHold.at <= a.padAimHoldMs) return setV(out, this.aimHold.x, this.aimHold.y);
    return setV(out, 0, 0);
  }

  /** Dual-rumble on the active pad; magnitudes 0..1. Silently ignored where unsupported. */
  rumble(weak: number, strong: number, durationMs: number): void {
    const act = this.pad?.vibrationActuator;
    if (!act || typeof act.playEffect !== 'function' || durationMs <= 0) return;
    try {
      void act.playEffect('dual-rumble', {
        startDelay: 0, duration: Math.round(durationMs), weakMagnitude: clamp01(weak), strongMagnitude: clamp01(strong),
      }).catch(() => undefined);
    } catch {
      // Some browsers throw synchronously for unsupported effects.
    }
  }

  private releaseAll(): void {
    for (let i = 0; i < PAD_BUTTONS; i++) {
      if (this.down[i] === 1) this.releases[i]++;
      this.down[i] = 0;
    }
    this.axes.fill(0);
  }
}

function hasInput(p: PadLike): boolean {
  for (const b of p.buttons) if (b.pressed || b.value > PRESS_THRESHOLD) return true;
  return Math.hypot(finite(p.axes[0]), finite(p.axes[1])) > ACTIVITY_STICK || Math.hypot(finite(p.axes[2]), finite(p.axes[3])) > ACTIVITY_STICK;
}

function finite(v: number | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(-1, Math.min(1, v)) : 0;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}
